/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import baseUrl from '../BaseUrl';
import { watchParam } from '../../context/UserSegmentContext';
import EmptyState from './states/EmptyState';
import ErrorState from './states/ErrorState';

/**
 * Aggregate "Active users by app version" panel (feature 053b). Ranked totals
 * (version → count + bar) for the watch/all segment, with a Mobile / Watch
 * toggle. Current-version snapshot.
 *
 * Always shows YESTERDAY (UTC) only — users active yesterday, by version —
 * independent of the page's date range (feature 138).
 *
 * Props: { segment }
 */
const TOP_N = 15; // show this many versions individually; consolidate the rest

/** Yesterday as YYYY-MM-DD in UTC (activity_time_utc calendar). */
export const yesterdayUtc = (now = new Date()) =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1)).toISOString().slice(0, 10);

const ActiveUsersVersionPanel = ({ segment }) => {
  const day = yesterdayUtc();
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [kind, setKind] = useState('mobile');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      const now = new Date();
      if (expire * 1000 < now.getTime()) {
        const r = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${r.data.accessToken}`;
        setToken(r.data.accessToken);
        setExpire(jwtDecode(r.data.accessToken).exp);
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = `${baseUrl}/admin/api/get-active-users-version-distribution?kind=${kind}&from_date=${day}&to_date=${day}${watchParam(segment)}`;
      const res = await axiosJWT.get(url);
      setRows(res.data.data || []);
    } catch {
      setError('We couldn’t load the version breakdown. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [kind, day, segment]);

  useEffect(() => { load(); }, [kind, day, segment]);

  // Consolidate the long tail so the ranked list stays readable.
  const display = [];
  if (rows.length > TOP_N) {
    const head = rows.slice(0, TOP_N);
    const tail = rows.slice(TOP_N);
    const otherUsers = tail.reduce((s, r) => s + (Number(r.users) || 0), 0);
    display.push(...head);
    display.push({ version: `Older / other (${tail.length} versions)`, users: otherUsers, _other: true });
  } else {
    display.push(...rows);
  }
  const maxUsers = display.reduce((m, r) => Math.max(m, Number(r.users) || 0), 0) || 1;
  const grandTotal = rows.reduce((s, r) => s + (Number(r.users) || 0), 0);

  return (
    <div className="au-panel" data-testid="auv-panel">
      <div className="au-panel__head">
        <h4 className="au-panel__title">Active users by app version · yesterday</h4>
        <span className="au-spacer" />
        <div className="auv-kind-toggle" role="group" aria-label="App version type">
          <button
            type="button"
            className={`auv-kind-btn${kind === 'mobile' ? ' is-active' : ''}`}
            data-testid="auv-kind-mobile"
            onClick={() => setKind('mobile')}
          >Mobile</button>
          <button
            type="button"
            className={`auv-kind-btn${kind === 'watch' ? ' is-active' : ''}`}
            data-testid="auv-kind-watch"
            onClick={() => setKind('watch')}
          >Watch</button>
        </div>
      </div>
      <p className="auv-caption" data-testid="auv-caption">Users active yesterday ({day}, UTC) by their current {kind === 'watch' ? 'watch' : 'mobile'} app version.</p>

      {loading ? (
        <p className="au-modal-empty">Loading…</p>
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : display.length === 0 ? (
        <EmptyState title="No version data" message="There is no active-user version data for this view yet." />
      ) : (
        <ul className="auv-dist-list">
          {display.map((r, i) => {
            const users = Number(r.users) || 0;
            const pct = grandTotal > 0 ? Math.round((users / grandTotal) * 100) : 0;
            const barPct = Math.round((users / maxUsers) * 100);
            return (
              <li className="auv-dist-row" data-testid="auv-dist-row" key={r.version || i}>
                <span className="auv-dist-label" title={r.version}>{r.version}</span>
                <span className="auv-dist-bar-wrap" aria-hidden="true">
                  <span className={`auv-dist-bar${r._other ? ' auv-dist-bar--other' : ''}`} style={{ width: `${barPct}%` }} />
                </span>
                <span className="auv-dist-count tnum">{users.toLocaleString()}</span>
                <span className="auv-dist-pct tnum">{pct}%</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ActiveUsersVersionPanel;
